// The session a task came from, as the session viewer shows it, opened at the task: a few messages leading into it,
// the task's own turns between "Task starts" and "Task ends" (its first message and each later user message tagged;
// where a fix rewrote one, what the candidate gets instead is under it), more either way on request, and links that
// jump to each tagged message. A subagent's task is its own run.
import { esc, get } from '../core'
import { renderTranscript, wireTranscript } from '../transcript'
import { compact } from './compact'

const PAGE = 20

type Msg = { role: string; text: string; tool_calls: unknown[]; results: unknown[] }
interface Marks { start: number; end: number; followUps: { index: number; turn: number }[]; extra: { start: number; end: number }[] }
interface Win { total: number; from: number; to: number; messages: Msg[]; marks: Marks }
/** What the candidate gets, by message index, where it differs from what the session says. */
type Given = Map<number, string>

const loaded = new Map<string, Win>()

export function sessionMount(): string {
  return '<div class="rp-sess-jumps" id="rpSessJumps"></div><div class="tr-card rp-scroll rp-scroll-tall rp-tx rp-sess" id="rpSess">' +
    '<div class="empty">Loading the session…</div></div>'
}

/** Fill the mount for task `t` (its first message and later messages, to tell what the candidate gets). */
export function loadSession(root: HTMLElement, t: { id: string; firstMessage: string; later: { user: string; turn: number | null }[] }): void {
  const box = root.querySelector('#rpSess') as HTMLElement | null
  if (!box) return
  const hit = loaded.get(t.id)
  if (hit) { paint(root, box, t, hit, 'task'); return }
  get('/api/replay/tasks/' + encodeURIComponent(t.id) + '/session')
    .then((w: any) => {
      if (!w || w.error || !box.isConnected) throw new Error(w?.error || 'gone')
      const win: Win = { total: w.total, from: w.from, to: w.from + w.messages.length, messages: w.messages, marks: w.marks }
      loaded.set(t.id, win)
      paint(root, box, t, win, 'task')
    })
    .catch(() => { if (box.isConnected) box.innerHTML = '<div class="empty">The session this task came from is not stored here.</div>' })
}

const norm = (s: string) => s.replace(/<system-reminder>[\s\S]*?<\/system-reminder>/gi, ' ').replace(/\s+/g, ' ').trim()

function given(t: Parameters<typeof loadSession>[1], w: Win): Given {
  const out: Given = new Map()
  const at = (i: number) => w.messages[i - w.from]
  const first = at(w.marks.start)
  if (first && norm(first.text) !== norm(t.firstMessage)) out.set(w.marks.start, t.firstMessage)
  for (const f of w.marks.followUps) {
    const m = at(f.index), mine = t.later.find((l) => l.turn === f.turn)
    if (m && mine && norm(m.text) !== norm(mine.user)) out.set(f.index, mine.user)
  }
  return out
}

/** Draw the loaded window. `keep`: 'task' opens at the task's start; 'top'/'bottom' keep the reader where they were
 *  after more was added above or below. */
function paint(root: HTMLElement, box: HTMLElement, t: Parameters<typeof loadSession>[1], w: Win, keep: 'task' | 'top' | 'bottom'): void {
  const was = box.scrollHeight - box.scrollTop
  const m = w.marks
  const inTask = (i: number) => (i >= m.start && i < m.end) || m.extra.some((x) => i >= x.start && i < x.end)
  const follow = new Map(m.followUps.map((f, k) => [f.index, k + 1]))
  const instead = given(t, w)
  // each message with its index; tool-only assistant messages join the card before them (never across a boundary)
  type Item = Msg & { i: number }
  const items: Item[] = w.messages.map((x, k) => ({ ...x, i: w.from + k }))
  const groups: Item[][] = []
  for (const it of items) {
    const prev = groups[groups.length - 1]
    const tagged = (i: number) => i === m.start || follow.has(i)
    const boundary = tagged(it.i) || it.i === m.end || (!!prev && tagged(prev[0]!.i))
    if (prev && !boundary && inTask(prev[0]!.i) === inTask(it.i)) prev.push(it)
    else groups.push([it])
  }
  let html = w.from > 0
    ? '<div class="rp-sess-more"><button type="button" class="btn" data-more="up">Load ' + Math.min(PAGE, w.from) + ' earlier</button>' +
      '<span class="tr-muted">' + w.from + ' earlier messages</span></div>' : ''
  for (const g of groups) {
    const i = g[0]!.i
    if (i === m.start) html += '<div class="rp-hist-sep" id="rpSessStart"><span>Task starts</span></div>'
    if (i === m.end) html += '<div class="rp-hist-sep rp-sess-end" id="rpSessEnd"><span>Task ends</span></div>'
    const tag = i === m.start ? 'first message' : follow.has(i) ? 'follow-up ' + follow.get(i) : ''
    html += '<div class="rp-sm' + (inTask(i) ? ' rp-sm-task' : ' rp-sm-out') + (tag ? ' rp-sm-tagged' : '') + '" data-i="' + i + '">' +
      (tag ? '<div class="rp-sm-tag">' + esc(tag) + '</div>' : '') +
      renderTranscript(compact(g)) +
      (instead.has(i) ? '<div class="rp-sm-given"><span>The candidate gets</span>' + esc(instead.get(i)!) + '</div>' : '') + '</div>'
  }
  if (w.to === m.end && w.to < w.total) html += '<div class="rp-hist-sep rp-sess-end" id="rpSessEnd"><span>Task ends</span></div>'
  if (w.to < w.total) {
    html += '<div class="rp-sess-more"><button type="button" class="btn" data-more="down">Load ' + Math.min(PAGE, w.total - w.to) + ' more</button>' +
      '<span class="tr-muted">' + (w.total - w.to) + ' later messages</span></div>'
  }
  box.innerHTML = html
  wireTranscript(box)
  if (keep === 'task') {
    const start = box.querySelector('#rpSessStart') as HTMLElement | null
    box.scrollTop = start ? Math.max(0, start.offsetTop - box.offsetTop - 120) : 0
  } else if (keep === 'top') {
    box.scrollTop = box.scrollHeight - was
  }
  Array.prototype.forEach.call(box.querySelectorAll('[data-more]'), (b: HTMLButtonElement) => {
    b.onclick = () => more(root, box, t, w, b.getAttribute('data-more') as 'up' | 'down')
  })
  jumps(root, box, t, w)
}

function more(root: HTMLElement, box: HTMLElement, t: Parameters<typeof loadSession>[1], w: Win, dir: 'up' | 'down', then?: () => void): void {
  const from = dir === 'up' ? Math.max(0, w.from - PAGE) : w.to
  const to = dir === 'up' ? w.from : Math.min(w.total, w.to + PAGE)
  fetchRange(t.id, from, to).then((msgs) => {
    const next: Win = dir === 'up'
      ? { ...w, from, messages: [...msgs, ...w.messages] }
      : { ...w, to: w.to + msgs.length, messages: [...w.messages, ...msgs] }
    loaded.set(t.id, next)
    if (box.isConnected) { paint(root, box, t, next, dir === 'up' ? 'top' : 'bottom'); then?.() }
  })
}

const fetchRange = (id: string, from: number, to: number): Promise<Msg[]> =>
  get('/api/replay/tasks/' + encodeURIComponent(id) + '/session?from=' + from + '&to=' + to).then((w: any) => (Array.isArray(w?.messages) ? w.messages : []))

/** Links to the task's start, each later user message and its end; one not loaded yet loads first. */
function jumps(root: HTMLElement, box: HTMLElement, t: Parameters<typeof loadSession>[1], w: Win): void {
  const bar = root.querySelector('#rpSessJumps') as HTMLElement | null
  if (!bar) return
  const m = w.marks
  const marks: [string, number][] = [['First message', m.start], ...m.followUps.map((f, k) => ['Follow-up ' + (k + 1) + ' · turn ' + f.turn, f.index] as [string, number])]
  if (m.end < w.total) marks.push(['Task ends', m.end])
  bar.innerHTML = marks.length > 1 ? marks.map(([label, i]) => '<button type="button" class="rp-fchip" data-to="' + i + '">' + esc(label) + '</button>').join('') : ''
  Array.prototype.forEach.call(bar.querySelectorAll('[data-to]'), (b: HTMLElement) => {
    b.onclick = () => {
      const i = Number(b.getAttribute('data-to'))
      const go = () => {
        const el = box.querySelector('[data-i="' + i + '"]') as HTMLElement | null
        if (el) box.scrollTop = el.offsetTop - box.offsetTop - 12
      }
      const cur = loaded.get(t.id) ?? w
      if (i >= cur.from && i < cur.to) go()
      else if (i >= cur.to) fetchRange(t.id, cur.to, Math.min(cur.total, i + 6)).then((msgs) => {
        const next = { ...cur, to: cur.to + msgs.length, messages: [...cur.messages, ...msgs] }
        loaded.set(t.id, next)
        paint(root, box, t, next, 'bottom')
        go()
      })
    }
  })
}
